import type { CartItem } from '@/components/account-cart-provider'
import type { FundingFormProgressRow } from '@/lib/application-progress-overrides'
import {
  cartItemsToAnalyticsProducts,
  fundingRowsToAnalyticsProducts,
  rollupProductFlags,
  type AnalyticsProduct,
  type ApplicationType,
} from '@/lib/analytics-product-context'
import {
  pushApplicationStarted,
  pushApplicationStep,
  pushDataLayerEvent,
  setApplicationContext,
} from '@/lib/gtm-data-layer'
import { trackEvent } from '@/lib/mixpanel'

export function trackAnalyticsEvent(
  mixpanelEventName: string,
  mixpanelProps?: Record<string, unknown>,
  gtmEvent?: {
    event: string
    payload: Record<string, unknown>
  },
): void {
  trackEvent(mixpanelEventName, mixpanelProps)

  if (gtmEvent) {
    pushDataLayerEvent(gtmEvent.event, gtmEvent.payload)
  }
}

export function resolveApplicationProducts(input: {
  cart?: CartItem[]
  fundingRows?: Array<FundingFormProgressRow>
  products?: AnalyticsProduct[]
}): AnalyticsProduct[] {
  if (input.products && input.products.length > 0) {
    return input.products
  }

  if (input.fundingRows && input.fundingRows.length > 0) {
    return fundingRowsToAnalyticsProducts(input.fundingRows)
  }

  if (input.cart && input.cart.length > 0) {
    return cartItemsToAnalyticsProducts(input.cart)
  }

  return []
}

export function syncApplicationAnalyticsContext(input: {
  applicationType: ApplicationType
  cart?: CartItem[]
  fundingRows?: Array<FundingFormProgressRow>
  applicationId?: string
  products?: AnalyticsProduct[]
}): AnalyticsProduct[] {
  const products = resolveApplicationProducts(input)

  setApplicationContext({
    application_type: input.applicationType,
    application_id: input.applicationId,
    products,
  })

  return products
}

export function trackApplicationStartedEvent(input: {
  applicationType: ApplicationType
  cart?: CartItem[]
  fundingRows?: Array<FundingFormProgressRow>
  applicationId?: string
  page?: string
  products?: AnalyticsProduct[]
}): void {
  const products = syncApplicationAnalyticsContext(input)
  const { product_ids, product_names } = rollupProductFlags(products)

  trackEvent('Application Started', {
    page_type: 'conversion',
    application_type: input.applicationType,
    page: input.page,
    product_ids,
    product_names,
  })

  pushApplicationStarted({
    application_type: input.applicationType,
    application_id: input.applicationId,
    products,
  })
}

export function trackApplicationFunnelStep(input: {
  step_name: string
  step_number: number
  funnel_name: string
  page: string
  applicationType: ApplicationType
  cart?: CartItem[]
  fundingRows?: Array<FundingFormProgressRow>
  applicationId?: string
  products?: AnalyticsProduct[]
  mixpanelExtras?: Record<string, unknown>
}): void {
  const products = resolveApplicationProducts({
    cart: input.cart,
    fundingRows: input.fundingRows,
    products: input.products,
  })
  const { product_ids, product_names } = rollupProductFlags(products)

  trackEvent('Funnel Step', {
    step: input.step_name,
    step_number: input.step_number,
    funnel_name: input.funnel_name,
    page: input.page,
    application_type: input.applicationType,
    product_ids,
    product_names,
    ...input.mixpanelExtras,
  })

  pushApplicationStep({
    step_name: input.step_name,
    step_number: input.step_number,
    funnel_name: input.funnel_name,
    page: input.page,
    application_type: input.applicationType,
    application_id: input.applicationId,
    products,
  })
}
